import AppleLogin from "react-apple-login";
import { useRecoilValue } from "recoil";
import { userSimpleAtom } from "recoil/userSimpleAtom";
import { useRef, useState } from "react";
import { isDev } from "../../../config";
import useAutoLoginV3 from "./useAutoLoginV3";
import { decodeJWT } from "../../../utils/jwtUtils";
import LoginButtonTemplatePromotion from "../../user/LoginButtonTemplatePromotion";
import useAppleJs from "../../../hooks/useAppleJs";
import useLoginV3Mutation from "./useLoginV3Mutation";

export default function AppleLoginButtonV3({
  render,
}: {
  render?: (props: {
    onClick: (e?: any) => void;
    disabled?: boolean;
  }) => JSX.Element;
}) {
  useAutoLoginV3();
  const user = useRecoilValue(userSimpleAtom);
  const [isLoading, setIsLoading] = useState(false);
  const { loginAsync } = useLoginV3Mutation("apple");
  const appleReady = useAppleJs();

  // Detect popup close when using usePopup: if the user closes the popup
  // without completing auth, callback won't fire. We listen for window focus
  // and clear loading state if no callback happened.
  const focusListenerRef = useRef<
    ((this: Window, ev: FocusEvent) => any) | null
  >(null);
  const callbackFiredRef = useRef(false);

  const clearFocusListener = () => {
    if (focusListenerRef.current) {
      window.removeEventListener("focus", focusListenerRef.current);
      focusListenerRef.current = null;
    }
  };

  const apple_cliendId = isDev
    ? "dev.orwellhealth.distancing.login"
    : "com.orwellhealth.distancing.login";

  const apple_redirectUrl = isDev
    ? "https://dev-www.distancing.im/v3/oauth/apple"
    : `${window.location.origin}/v3/oauth/apple`;

  // If Apple JS SDK isn't ready yet, render a no-op button
  if (!appleReady) {
    return (
      <LoginButtonTemplatePromotion
        loginType="apple"
        action={() => {}}
        isLoggedIn={!!user?.accessToken}
        isLoading={false}
      />
    );
  }

  return (
    <AppleLogin
      clientId={apple_cliendId}
      redirectURI={apple_redirectUrl}
      responseType="code id_token"
      responseMode="form_post"
      scope="email"
      usePopup
      callback={(response: any) => {
        try {
          // mark that callback fired so focus handler won't treat as cancel
          callbackFiredRef.current = true;
          clearFocusListener();
          const idToken = response?.authorization?.id_token;
          if (idToken) {
            const decodedToken = decodeJWT(idToken);
            const email = decodedToken?.email || undefined;
            // redirect 없이 바로 로그인 처리
            loginAsync({ idToken, email })
              .catch(() => {})
              .finally(() => {
                setIsLoading(false);
                clearFocusListener();
              });
          } else {
            setIsLoading(false);
          }
        } catch (e) {
          setIsLoading(false);
        }
      }}
      render={
        render
          ? render
          : (props) => (
              <LoginButtonTemplatePromotion
                loginType="apple"
                action={() => {
                  setIsLoading(true);
                  // Prepare focus listener to detect popup close without callback
                  callbackFiredRef.current = false;
                  const onFocus = () => {
                    setTimeout(() => {
                      if (!callbackFiredRef.current) {
                        // likely popup closed/cancelled
                        setIsLoading(false);
                        clearFocusListener();
                      }
                    }, 300);
                  };
                  focusListenerRef.current = onFocus;
                  window.addEventListener("focus", onFocus);
                  props.onClick();
                }}
                isLoggedIn={!!user?.accessToken}
                isLoading={isLoading}
              />
            )
      }
    />
  );
}
